import { useTranslation } from "react-i18next";
import { PlayIcon, XIcon } from "@heroicons/react/outline";
import { useAppDispatch, useAppSelector } from "../../../common/state/hooks";
import { getStepPosition, getTour } from "../registry";
import type { ResumableTour } from "../state/onboardingSlice";
import { useRouteNeedsUnloadedEntity } from "../hooks/useRouteNeedsUnloadedEntity";
import { resumeTour, selectOnboardingAudience, skipTour } from "../state/onboardingSlice";

export type TourResumePillProps = {
  resumable: ResumableTour;
};

export const TourResumePill = ({ resumable }: TourResumePillProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const audience = useAppSelector(selectOnboardingAudience);
  const restarts = useRouteNeedsUnloadedEntity()(resumable.route);
  const tour = getTour(resumable.id, audience);

  if (!tour) {
    return null;
  }

  const handleResume = (): void => {
    void dispatch(resumeTour({ id: resumable.id, source: "tour" }));
  };

  const handleDismiss = (): void => {
    void dispatch(skipTour({ id: resumable.id, step: resumable.step }));
  };

  return (
    <div
      role="status"
      data-testid="onboarding-resume-pill"
      className="flex items-center h-10 rounded-full bg-blue-500 shadow-lg"
    >
      <button
        type="button"
        className="flex items-center gap-2 h-full pl-3 pr-2 rounded-l-full hover:bg-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
        onClick={handleResume}
      >
        <PlayIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
        <span className="text-sm font-semibold text-white max-w-[26rem] truncate">
          {restarts
            ? t("onboarding.resume.restartTitle", { tour: t(tour.titleKey) })
            : t("onboarding.resume.title")}
        </span>
        {!restarts && (
          <span className="text-sm text-white/70">
            {t("onboarding.resume.progress", getStepPosition(tour, resumable.step))}
          </span>
        )}
      </button>
      <button
        type="button"
        className="h-full pl-1 pr-3 rounded-r-full text-white/60 hover:text-white hover:bg-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
        onClick={handleDismiss}
        aria-label={t("onboarding.resume.dismissAriaLabel")}
      >
        <XIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
};

export default TourResumePill;
